<script setup>
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { useCustomerStore } from "@/stores/CustomersStore"
import { useModalStore } from "@/stores/ModalStore"
import { useEventStore } from "@/stores/EventsStore"
import { useReserveListStore } from "@/stores/ReserveListStore"
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue"
import SalesHandler from "@/components/sales/SalesHandler.vue"
import { extractDate } from "@/utils/datetime"
import emitter from '@/utils/emitter'


/* ---------------------------- 👉 Props ---------------------------- */
const props = defineProps({
  jumpToDateFc: Function,
  //refetchEvents: Function,
})


/* ---------------------------- 👉 i18n & Field Validation ---------------------------- */
const { t } = useI18n()


/* ---------------------------- Vuetify Mobile Breakpoint & Dialog Width ---------------------------- */
const { mobile } = useDisplay()

/* ---------------------------- 👉 Stores ---------------------------- */
const THIS_MODAL_NAME = 'calendarSales'
const customerStore = useCustomerStore()
const eventStore = useEventStore()
const modalStore = useModalStore()
const calendarStore = useCalendarStore()
const reserveListStore = useReserveListStore()

/* ---------------------------- 👉 Refs ---------------------------- */
//const refForm = ref() // Main event form

const isRegularVisit = ref(null) // Singular / Cyclic

// 👉 Form fields controls
const isAmountExceeded = ref(false) // prepayment amount tracker


const visitHandlerElement = ref(null) // Main dialog element
const isMainDialogMinimized = ref(false)

/* ---------------------------- 👉 Computed Properties ---------------------------- */
// isNormalVisit must be computed in case you click on next event on calendar without closing modal 1st
// eventStore.selectedEvent.type: 0 - normal, 1 - note, 2 - blockage
//const isNormalVisit = computed(() => eventStore.selectedEvent?.type?.toString() === "0")

/* ---------------------------- 👉 Methods ---------------------------- */


// Delete a 'store-selling' item
const deleteSales = async () => {

  const confirmed = await modalStore.openConfirmationModal(t('sales.confirm_deletion'), t('warning'))

  if (confirmed) {
    try {
      const response = await customerStore.deleteProductSale(eventStore.selectedEvent.id)

      if (response.success) {
        emitter.emit('sale-changed', eventStore.selectedEvent.id)
        modalStore.closeModal(THIS_MODAL_NAME)
        // await nextTick(() => {
        //   refForm.value?.reset()
        //   refForm.value?.resetValidation()
        // })
      }
    } catch (e) {
      console.error('Error deleting selling:', e)
    } finally {

    }
  }

}

/**
 * Resets the event data and form validations.
 * Removes temporary event from the calendar if it exists.
 * @async
 */
const resetEvent = async (clearSelectedCustomer = true) => {
  // console.warn('resetEvent1', eventStore.selectedEvent.id)
  isRegularVisit.value = false
  eventStore.blockadeOrNoteView = false
  eventStore.cyclicVisitView = false
  eventStore.visitView = true
  await eventStore.resetSelectedEvent(clearSelectedCustomer)
  await nextTick() // Wait for Vue to propagate reactivity

  //refForm.value?.resetValidation()
  // customerStore.selectedCustomer = null
  // eventStore.selectedServices = []
  if (calendarStore.selectedCalendars.length)
    eventStore.selectedEmployee.value = calendarStore.selectedCalendars[0]

  eventStore.allowSave = true
  eventStore.isSaving = false

}

/**
 * Cancels the event form.
 */
const onCancel = async () => {

  if(eventStore.allowSave && eventStore.isModified) {
    const confirmed = await modalStore.openConfirmationModal(t('are_you_sure_you_want_to_close_this_window'), t('visit_was_modified'))
    if(!confirmed) return
  }

  modalStore.closeModal(THIS_MODAL_NAME)

  // await nextTick(() => {
  //   if(eventStore.isModified) {
  //     props.refetchEvents()
  //   }
  // })

  // await nextTick(() => {
  //   refForm.value?.reset()
  //   //resetEvent()
  //   refForm.value?.resetValidation()
  // })
}


const eventContentElement = ref()


/* ---------------------------- 👉 Watchers ---------------------------- */

//👉 Watch for dialog type
const dialogWidth = computed(() => {
  if (mobile.value) return '100%'

  return '1680px'
})

/* ---------------------------- 👉 Lifecycle Hooks ---------------------------- */
onMounted(async () => {

  eventStore.setAllowEndTimeRecalculation(false)
  // window.addEventListener('keydown', closeModalWithEscKey)

  setTimeout(() => {
    eventStore.isWatcherActive = true // Start watching for changes
  }, 500)


})

onBeforeUnmount(async () => {

  // Store current scroll position
  eventStore.isWatcherActive = false // Stop watching for changes
  eventStore.setAllowEndTimeRecalculation(false)
  await resetEvent(!modalStore.modals.customerCardDialog)


})

// Validate business logic
const saveBusinessValidator = () => {

  if(eventStore.selectedProducts.length === 0) {
    showWarningToast(t('save_event_business_validation.products_are_empty'))

    return false
  }

  if(Number(eventStore.balanceAmount) < 0) {
    showWarningToast(t('save_event_business_validation.balance_is_negative'))

    return false
  }

  // Check BON & KARNET same time
  let isError = false
  const voucherMethods = eventStore.selectedPaymentMethods.filter(m => m.name === 'bon_saloner' && m.voucher?.checkStatus !== undefined && m.voucher?.checkStatus !== 2)
  if (voucherMethods.length > 0) {
    showWarningToast(t('save_event_business_validation.incorrect_voucher_code_inputted'))
    isError = true
  }
  const passMethods = eventStore.selectedPaymentMethods.filter(m => m.name === 'pass_saloner' && m.voucher?.checkStatus !== undefined && m.voucher?.checkStatus !== 2)
  if (passMethods.length > 0) {
    console.log('passMethods', passMethods)
    showWarningToast(t('save_event_business_validation.incorrect_pass_code_inputted'))
    isError = true
  }
  if (isError) return false

  // Check if we have enough cash money to give back to client #869b2083f
  if(Number(eventStore.balanceAmount) > 0) {

    if(!eventStore.selectedPaymentMethods.find(m => m.name === 'money')) {
      showWarningToast(t('save_event_business_validation.cant_give_back_change_when_cash_is_not_selected'))

      return false
    }

    if(Number(eventStore.balanceAmount) > eventStore.selectedPaymentMethods.find(m => m.name === 'money').amount) {
      showWarningToast(t('save_event_business_validation.change_cannot_be_larger_then_cash_amount'))

      return false
    }


  }

  return true
}


const saveSale = async () => {
  //showErrorToast('TODO')
  if(!saveBusinessValidator()) return false
  eventStore.isSaving = true
  try {
    let response = {}

    response = await $backend('/productSales/sell', {
      body: {
        id: eventStore.selectedEvent.id,
        dateStart: extractDate(eventStore.selectedDate, '-'),
        timeStart: eventStore.selectedStartTime,
        description: eventStore.selectedEvent.description,
        customer: customerStore.selectedCustomer,
        products: eventStore.selectedProducts,
        paymentMethods: eventStore.selectedPaymentMethods,
        paymentDetails: {
          finalPrice: eventStore.finalPrice,
          tip: Number(eventStore.tipAmount),
          toGiveBack: eventStore.balanceAmount,
          taken: eventStore.paymentMethodsTotal,
          loyaltyPoints: Number(eventStore.loyaltyPointsEarned) || 0, //TODO w saveVisit i saveSale
          discount: Number(eventStore.finalPriceWithNoDiscount - eventStore.finalPrice) || 0,

        },
        loyaltyProgramCoupon: eventStore.selectedLoyaltyCoupon,
      },
      method: 'POST',
    })

    if(response.success) {
      showSuccessToast(response.message )
      modalStore.closeModal(THIS_MODAL_NAME)
      emitter.emit('sale-changed', eventStore.selectedEvent?.id)
    } else {
      showErrorToast(response.message ?? t('fetch_errors.unhandled_error')) //B: this should practically never show
    }


  }
  catch(error){
    console.error('Error while saving event:', error)
  }
  finally {
    eventStore.isSaving = false
  }
}

/**
 * Unlocks finalized sale
 */
const unlockFinalizedSale = async () => {
  eventStore.wasFinalisedAlready = true
  eventStore.allowSave = true
}
</script>

<template>
  <VDialog
    ref="visitHandlerElement"
    :content-class="[
      'main-event-dialog',
      'ma-0',
      'border-thin',
      { 'expanded': mobile && isMainDialogMinimized === true },
      { 'rounded-0 mobile-mode': mobile },
      { 'rounded-xl': !mobile },
      { 'minimized': isMainDialogMinimized === true },
    ]"
    :model-value="modalStore.modals.calendarSales"
    :max-width="dialogWidth"
    :min-width="!mobile ? '800px' : ''"
    :scrim="isMainDialogMinimized === false"
    persistent
    scrollable
    :fullscreen="mobile"
    transition="dialog-bottom-transition"
    :loading="eventStore.isLoading || eventStore.isSaving"
  >
    <!-- 👉 Parent modal header -->
    <VSheet
      min-height="60px"
      max-height="max-content"
      :class="{
        'highlighted': isMainDialogMinimized === true,
        'rounded-0 ': mobile,
        'rounded-t-xl rounded-b-0': !mobile,
      }"
      class="
      main-dialog-top
      d-flex
      justify-space-between
      align-center
      px-4
      py-2
      overflow-y-hidden
      "
    >
      <!--      v-on="mouseEvents"-->

      <div class="text-h5 d-flex align-center justify-space-between flex-wrap ga-1 w-100">
        <div>
          {{
            eventStore.selectedEvent.id ? $t('sales.edit') : $t('sales.new')
          }}
        </div>
      </div>

      <div class="d-flex align-start h-100">
        <!--        <VBtn-->
        <!--          :ripple="false"-->
        <!--          size="large"-->
        <!--          variant="plain"-->
        <!--          color="grey"-->
        <!--          class="px-1 ml-2"-->
        <!--          @click="handleDialogMinimize"-->
        <!--        >-->
        <!--          <span-->
        <!--            v-if="isMainDialogMinimized"-->
        <!--            class="text-caption text-high-emphasis"-->
        <!--          >-->
        <!--            {{ !mobile ? $t('unwrap') : '' }}-->
        <!--          </span>-->
        <!--          <span-->
        <!--            v-else-->
        <!--            class="text-caption text-high-emphasis"-->
        <!--          >-->
        <!--            {{ !mobile ? $t('wrap') : '' }}-->
        <!--          </span>-->
        <!--          <template #append>-->
        <!--            <VIcon-->
        <!--              class="ml-n2"-->
        <!--              :icon="isMainDialogMinimized ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"-->
        <!--            />-->
        <!--          </template>-->
        <!--        </VBtn>-->


        <span>
          <VBtn
            size="large"
            variant="plain"
            color="grey-darken-3"
            icon="ri-close-line"
            class="text-high-emphasis"
            @click="onCancel"
          />
          <VTooltip activator="parent">
            {{ $t('close_window') }}
          </VTooltip>
        </span>
      </div>
    </VSheet>

    <VCard
      ref="eventContentElement"
      class="event-content rounded-t-0 minimizable border-none "
      :class="{'flex-fill justify-space-between': mobile}"
    >
      <VSkeletonLoader
        v-if="eventStore.isLoading"
        elevation="24"
        class="mx-auto bg-white"
        width="100%"
        type="table-heading,image,image,image,image,divider,actions"
      />
      <template v-else>
        <VProgressCircular
          v-if="eventStore.isSaving"
          class="position-absolute centered"
          color="primary"
          indeterminate
          size="50"
          style="z-index: 9999"
        />
        <div
          v-if="eventStore.isSaving"
          class="position-absolute w-100 h-100"
          style="background-color: rgba(var(--v-theme-background), 0.3); z-index: 9998;"
        >
          &nbsp;
        </div>
        <VDivider />

        <VCardText
          v-if="eventStore.visitView"
          class="event-dialog-card-text"
        >
          <SalesHandler :jump-to-date-fc="jumpToDateFc" />
        </VCardText>


        <!-- 👉 Main dialog actions -->
        <VCardActions class="pt-2 border-t">
          <!--left-->
          <div class="d-flex justify-start flex-fill ga-2 pa-2">
            <!-- Anuluj -->
            <VBtn
              :disabled="eventStore.isSaving"
              :size="mobile ? 'small' : 'default'"
              variant="outlined"
              color="secondary"
              @click="onCancel"
            >
              {{ $t('cancel') }}
              <VTooltip activator="parent">
                {{ eventStore.selectedEvent.id ? $t('cancel_edit') : $t('cancel') }}
              </VTooltip>
            </VBtn>
            <!-- Usuń  sprzedaż-->
            <VBtn
              v-show="eventStore.selectedEvent.id && eventStore.actions.showDelete"
              :disabled="eventStore.isSaving"
              :size="mobile ? 'small' : 'default'"
              variant="plain"
              color="secondary"
              prepend-icon="ri-delete-bin-line"
              @click="deleteSales"
            >
              {{ $t('delete') }}
              <VTooltip activator="parent">
                {{
                  $t('sales.delete')
                }}
              </VTooltip>
            </VBtn>
          </div>


          <!--right-->
          <!-- Odblokuj sfinalizowana -->
          <VBtn
            v-if="eventStore.showFinalization && !eventStore.allowSave && eventStore.actions.showEdit"
            :disabled="eventStore.isSaving"
            :size="mobile ? 'small' : 'default'"
            variant="outlined"
            color="secondary"
            prepend-icon="ri-lock-unlock-line"
            @click="unlockFinalizedSale"
          >
            {{ $t('edit') }}
            <VTooltip activator="parent">
              {{ $t('edit_visit') }}
            </VTooltip>
          </VBtn>
          <!-- Zapisz  sprzedaz -->
          <VBtn
            v-if="eventStore.allowSave"
            :disabled="eventStore.isSaving"
            :size="mobile ? 'small' : 'default'"
            variant="flat"
            color="green"
            @click="saveSale"
          >
            {{ $t('sale_finalise') }}
            <VTooltip activator="parent">
              {{ $t('sale_save_add_issue_a_receipt') }}
            </VTooltip>
          </VBtn>
        </VCardActions>
      </template>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
#servicesList {
  -ms-overflow-style: none;  /* For Internet Explorer and Edge */
  scrollbar-width: none !important;     /* For Firefox */
  overflow-y: scroll;
  ::-webkit-scrollbar {
    display: none;
    scrollbar-width: none;
  }
}
//.event-content {
//  border: thin !important;
//}
.main-dialog-top {
  border: thin solid transparent;
}

.highlighted {
  border-color: rgba(241, 79, 22, 0.4);
  box-shadow: 0 0 10px 2px rgba(241, 79, 22, 0.3) !important;
  border-width: 2px;

}
.main-event-dialog {
  transition:  0.35s;
  scroll-behavior: auto;
  overflow-anchor: none;
}
.main-event-dialog.minimized {
  top: calc(100% - 60px) !important;
}

.main-event-dialog.mobile-mode {
  width: 100vw;
  height: 100dvh;
  top: 0;
}

.main-event-dialog.expanded {
  min-height: 100vh;
}

.list-item-mobile-layout {
  .v-list-item {
    grid-template-columns: 1fr;
    grid-template-areas: unset;
    gap: .5rem;

    &__content, &__append {
      grid-area: unset;
    }
  }

  .v-list-item-subtitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .v-list-item__append {
    justify-content: space-between;
  }

  .v-avatar-group {
    margin-right: auto;
  }
}

body .fc .fc-toolbar-chunk .fc-button-group .fc-today-button,
body .fc .fc-toolbar-chunk .fc-button-group .fc-today-button:hover,
body .fc .fc-toolbar-chunk .fc-button-group .fc-today-button:not(.disabled):active {
  border: 1px solid rgb(241, 79, 22, 0.5);
  border-radius: 6px;
}

.fc-timegrid-col {
  //padding-right: 18px !important;
}
.fc-timegrid-event-harness {
  margin-right: 10px;
}

.highlighted-focus:focus {
  border: 2px dotted rgb(241, 79, 22);
}

.v-field--appended {
  padding-inline-end: 10px;
}

/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity .2s linear;
  max-height: 1200px;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  max-height: 0;
}
.pulsating-icon {
  animation: pulse 1.25s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    opacity: 0.7;
    transform: scale(0.80);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.7;
    transform: scale(0.80);
  }
}
</style>

